import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Edit, Trash2, UserCheck, Phone, RefreshCw } from 'lucide-react';
import ParentDetailsModal from '@/components/admin/ParentDetailsModal';
import StudentUpdateModal from '@/components/admin/StudentUpdateModal';

const StudentCard = ({ student, userRole, onEdit, onDelete, onAssign, onConnect, onRefresh }) => {
  const navigate = useNavigate();
  const [showParentModal, setShowParentModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);


  const handleCardClick = (e) => {
    // Don't navigate if clicking on action buttons or inside modals
    if (e.target.closest('.action-button') ||
      e.target.closest('[role="dialog"]') ||
      e.target.closest('.modal-content')) {
      return;
    }

    // Navigate to student profile
    if (userRole === 'admin') {
      navigate(`/admin/students/${student.id}`);
    } else if (userRole === 'warden') {
      navigate(`/warden/students/${student.id}`);
    }
  };

  const handleConnectClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowParentModal(true);
  };

  const handleUpdateClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowUpdateModal(true);
  };

  const handleCloseModal = () => {
    setShowParentModal(false);
    setShowUpdateModal(false);
    // Refresh student data if callback provided
    if (onRefresh) {
      onRefresh();
    }
  };

  return (
    <>
      <div
        className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden cursor-pointer"
        onClick={handleCardClick}
        id="student-card"
      >
        {/* Card Header with Photo */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 relative">
          <div className="flex items-center gap-4">
            {student.photo_url ? (
              <img
                src={student.photo_url}
                alt={student.name}
                className="w-16 h-16 rounded-full border-4 border-white object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
                <User className="w-8 h-8 text-blue-500" />
              </div>
            )}
            <div className="flex-1 text-white">
              <h3 className="text-lg font-bold">{student.name}</h3>
              <p className="text-sm opacity-90">{student.readable_id || student.id}</p>
            </div>
          </div>
        </div>

        {/* Card Body */}
        <div className="p-4">
          <div className="grid grid-cols-2 gap-3 text-sm mb-3">
            <div>
              <span className="text-gray-600">Age:</span>
              <span className="ml-2 font-semibold">{student.age}</span>
            </div>
            <div>
              <span className="text-gray-600">Class:</span>
              <span className="ml-2 font-semibold">{student.class_name}</span>
            </div>
            <div>
              <span className="text-gray-600">Room:</span>
              <span className="ml-2 font-semibold">{student.room_number}</span>
            </div>
            <div>
              <span className="text-gray-600">Warden:</span>
              {Array.isArray(student.assigned_wardens) && student.assigned_wardens.length > 0 ? (
                <span
                  className="ml-2 text-xs px-2 py-1 bg-green-100 text-green-700 rounded"
                  title={student.assigned_wardens.map(w => w.name).join(', ')}
                >
                  {student.assigned_wardens.length <= 2
                    ? student.assigned_wardens.map(w => w.name).join(', ')
                    : `${student.assigned_wardens[0].name}, ${student.assigned_wardens[1].name} +${student.assigned_wardens.length - 2}`}
                </span>
              ) : (student.warden_name || student.warden_info?.name) ? (
                <span className="ml-2 text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                  {student.warden_name || student.warden_info.name}
                  {student.secondary_warden_name || student.secondary_warden_info?.name
                    ? `, ${student.secondary_warden_name || student.secondary_warden_info.name}`
                    : ''}
                </span>
              ) : student.secondary_warden_name || student.secondary_warden_info?.name ? (
                <span className="ml-2 text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                  {student.secondary_warden_name || student.secondary_warden_info.name}
                </span>
              ) : (
                <span className="ml-2 text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                  Not Assigned
                </span>
              )}
            </div>
          </div>

          {/* Interests */}
          {student.interests && student.interests.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-gray-600 mb-1">Interests:</p>
              <div className="flex flex-wrap gap-1">
                {student.interests.slice(0, 3).map((interest, idx) => (
                  <span
                    key={idx}
                    className="text-xs px-2 py-1 bg-pink-50 text-pink-600 rounded"
                  >
                    {interest}
                  </span>
                ))}
                {student.interests.length > 3 && (
                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                    +{student.interests.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {(userRole === 'admin' || userRole === 'warden') && (
            <div id="action-buttons"
              className={`border-t pt-3 mt-3 transition-opacity duration-200`}
            >
              <div className="flex flex-wrap gap-2">
                {/* Edit Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit && onEdit(student);
                  }}
                  className="action-button flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors text-sm"
                  title="Edit Student"
                >
                  <Edit className="w-3.5 h-3.5" />
                  <span>Edit</span>
                </button>

                {/* Assign Warden (Admin only) */}
                {userRole === 'admin' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAssign && onAssign(student);
                    }}
                    className="action-button flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-600 rounded hover:bg-green-100 transition-colors text-sm"
                    title="Assign Warden"
                  >
                    <UserCheck className="w-3.5 h-3.5" />
                    <span>Assign</span>
                  </button>
                )}

                {/* Connect Button - Show Parent Details */}
                {student.parent_id && (
                  <button
                    onClick={handleConnectClick}
                    className="action-button flex items-center gap-1 px-3 py-1.5 bg-purple-50 text-purple-600 rounded hover:bg-purple-100 transition-colors text-sm"
                    title="Connect with Parent"
                    data-testid="student-connect-btn"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    <span>Connect</span>
                  </button>
                )}

                {/* Update Button - Update Records */}
                <button
                  onClick={handleUpdateClick}
                  className="action-button flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors text-sm"
                  title="Update Student Records"
                  data-testid="student-update-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span>Update</span>
                </button>

                {/* Delete (Admin only) */}
                {userRole === 'admin' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete && onDelete(student);
                    }}
                    className="action-button flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors text-sm"
                    title="Delete Student"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Delete</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Parent Details Modal - Render outside card to prevent click bubbling */}
      {showParentModal && (
        <ParentDetailsModal
          isOpen={showParentModal}
          onClose={handleCloseModal}
          student={student}
        />
      )}

      {/* Student Update Modal - Render outside card to prevent click bubbling */}
      {showUpdateModal && (
        <StudentUpdateModal
          isOpen={showUpdateModal}
          onClose={handleCloseModal}
          student={student}
          onSuccess={handleCloseModal}
        />
      )}
    </>
  );
};

export default StudentCard;
